<template>
    <dialog-modal :show="showModal" @close="closeModal">
        <template #title>
            {{ $t("tenants.update.title") }}
        </template>

        <template #content class="bg-gray-50">
            <airtable-selector
                :errors="errorBags.default ? errorBags.default.airtable_record_id : false"
                type="tenants"
                key-name="name"
                class="my-4"
                @input="handleAirtableRecord"
            />

            <label for="id" class="block text-sm font-medium text-gray-700">
                {{ $t("tenants.forms.path.title") }}
            </label>
            <div class="mt-1 flex rounded-md shadow-sm" :class="{ 'border border-red-500': errors.id }">
                <span
                    class="
                        inline-flex
                        items-center
                        px-3
                        rounded-l-md
                        border border-r-0 border-gray-300
                        bg-gray-50
                        text-gray-500
                        sm:text-sm
                    "
                >
                    {{ env.url }}
                </span>
                <input
                    id="id"
                    v-model="currentTenant.id"
                    type="text"
                    disabled
                    name="id"
                    class="
                        flex-1
                        min-w-0
                        block
                        w-full
                        px-3
                        py-2
                        bg-gray-50
                        text-gray-500
                        rounded-none rounded-r-md
                        focus:ring-indigo-500
                        focus:border-indigo-500
                        sm:text-sm
                        border-gray-300
                    "
                />
            </div>

            <label class="block text-sm font-medium text-gray-700"> Logo </label>
            <p class="text-xs font-medium text-gray-700">
                {{ $t("tenants.forms.file.update_text") }}
            </p>

            <img
                v-if="!data.newImage"
                class="p-4 bg-gray-50 max-h-16 max-w-16 rounded hover:opacity-50 cursor-pointer"
                :src="currentTenant.image_url"
                alt=""
                @click="data.newImage = !data.newImage"
            />

            <div
                v-if="data.newImage"
                :class="{ 'border-red-500': errors.image }"
                class="max-w-lg flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md"
            >
                <div class="space-y-1 text-center">
                    <svg
                        class="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                    >
                        <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                    <div class="flex text-sm text-gray-600">
                        <label
                            for="file-upload"
                            class="
                                relative
                                cursor-pointer
                                bg-white
                                rounded-md
                                font-medium
                                text-indigo-600
                                hover:text-indigo-500
                                focus-within:outline-none
                                focus-within:ring-2
                                focus-within:ring-offset-2
                                focus-within:ring-indigo-500
                            "
                        >
                            <span>{{ $t("tenants.forms.file.title") }}</span>
                            <input
                                id="file-upload"
                                name="image"
                                type="file"
                                class="sr-only"
                                @input="currentTenant.image = $event.target.files[0]"
                            />
                        </label>
                        <p class="pl-1">{{ $t("tenants.forms.file.alt") }}</p>
                    </div>
                    <p class="text-xs text-gray-500">
                        {{ $t("tenants.forms.file.extensions") }}
                    </p>
                </div>
            </div>

            <p
                v-if="data.newImage"
                class="text-xs font-medium text-red-500 my-2 cursor-pointer"
                @click="data.newImage = !data.newImage"
            >
                {{ $t("tenants.forms.file.cancel_text") }}
            </p>
        </template>

        <template #footer class="bg-gray-50">
            <div class="flex justify-between">
                <button-secondary
                    button-style="danger"
                    class="mr-auto"
                    @click="closeModal"
                    @button-click="GoBackToIndex()"
                    >{{ $t("incidents.buttons.cancel") }}
                </button-secondary>
                <button-primary @click="updateTenant">{{ $t("tenants.buttons.update") }}</button-primary>
            </div>
        </template>
    </dialog-modal>
</template>

<script>
import { reactive } from "vue";
import { Inertia } from "@inertiajs/inertia";
import AirtableSelector from "@/Components/AirtableSelector";
import ButtonPrimary from "@/Components/ButtonPrimary";
import ButtonSecondary from "@/Components/ButtonSecondary";
import DialogModal from "@/Jetstream/DialogModal";
import env from "../../env";

export default {
    name: "TenantUpdate",
    components: {
        AirtableSelector,
        ButtonPrimary,
        DialogModal,
        ButtonSecondary,
    },
    props: {
        tenant: Object,
        errors: Object,
        errorBags: Object,
        showModal: Boolean,
    },
    emits: ["close"],
    setup(props, { emit }) {
        const currentTenant = Inertia.form({
            _method: "put",
            id: props.tenant ? props.tenant.data.id : "",
            airtable_record_id: props.tenant ? props.tenant.data.airtable_record_id : "",
            image_url: props.tenant ? props.tenant.data.image_url : "",
        });

        const data = reactive({
            newImage: false,
        });

        const updateTenant = () => {
            Inertia.post(route("universal.tenants.update", { tenant: props.tenant.data }), currentTenant, {
                forceFormData: true,
                onSuccess: () => emit("close", false),
            });
        };

        const GoBackToIndex = () => {
            Inertia.get(route("universal.tenants.index"));
        };

        const handleAirtableRecord = (value) => {
            currentTenant.value.airtable_record_id = value;
        };

        const closeModal = () => {
            Inertia.visit(route("universal.tenants.index"));
        };

        return {
            currentTenant,
            data,
            updateTenant,
            handleAirtableRecord,
            closeModal,
            GoBackToIndex,
            env,
        };
    },
};
</script>
